import { useEffect, useState } from 'react';
import {
  Typography, Button, Paper, Chip, Stack, TextField,
  List, ListItem, ListItemText, Tabs, Tab, Alert, Box, Divider,
} from '@mui/material';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import PublicLayout from '../components/PublicLayout';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

function deadlineLabel(deadline) {
  if (!deadline) return null;
  const ms = new Date(deadline).getTime() - Date.now();
  if (ms <= 0) return 'Deadline passed';
  const days = Math.floor(ms / (1000 * 60 * 60 * 24));
  if (days >= 1) return `${days} day${days === 1 ? '' : 's'} remaining`;
  const hours = Math.floor(ms / (1000 * 60 * 60));
  return `${hours} hour${hours === 1 ? '' : 's'} remaining`;
}

export default function PublicTenderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tender, setTender] = useState(null);
  const [error, setError] = useState('');
  const [tab, setTab] = useState(0);
  const [clarifications, setClarifications] = useState([]);
  const [question, setQuestion] = useState('');
  const [qMsg, setQMsg] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/tenders/${id}`);
        setTender(data.data);
      } catch {
        setError('This tender could not be found or is no longer public.');
      }
    })();
  }, [id]);

  const loadClarifications = async () => {
    try {
      const { data } = await api.get(`/tenders/${id}/clarifications`);
      setClarifications(data.data);
    } catch { /* ignore */ }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadClarifications(); }, [id]);

  const askQuestion = async () => {
    setQMsg('');
    if (!question.trim()) return;
    try {
      await api.post(`/tenders/${id}/clarifications`, { question });
      setQuestion('');
      setQMsg('Your question has been submitted.');
      loadClarifications();
    } catch (err) {
      setQMsg(err.response?.data?.error?.message || 'Could not submit question. You may need to sign in as a vendor.');
    }
  };

  const isVendor = user?.role === 'VENDOR';

  return (
    <PublicLayout maxWidth="md">
      <Button onClick={() => navigate(user?.role === 'VENDOR' ? '/vendor/tenders' : '/portal')} sx={{ mb: 2 }}>← Back to tenders</Button>

      {error && <Paper sx={{ p: 3 }}><Typography color="error">{error}</Typography></Paper>}

      {tender && (
        <Paper sx={{ p: 0, overflow: 'hidden' }}>
          <Box sx={{ p: 4, pb: 2 }}>
            <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
              <Chip label={tender.reference} size="small" color="primary" />
              <Chip label={tender.category} size="small" />
              {deadlineLabel(tender.submissionDeadline) && (
                <Chip label={deadlineLabel(tender.submissionDeadline)} size="small" color="secondary" />
              )}
            </Stack>
            <Typography variant="h4" gutterBottom>{tender.title}</Typography>
            <Typography variant="subtitle1" color="text.secondary">{tender.mda?.name}</Typography>
          </Box>

          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 4, borderBottom: 1, borderColor: 'divider' }}>
            <Tab label="Overview" />
            <Tab label="Tender Documents" />
            <Tab label="Clarifications" />
            <Tab label="Submit Bid" />
          </Tabs>

          <Box sx={{ p: 4 }}>
            {tab === 0 && (
              <>
                <Typography variant="h6" gutterBottom>Description</Typography>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mb: 3 }}>{tender.description}</Typography>
                {tender.eligibility && (
                  <>
                    <Typography variant="h6" gutterBottom>Eligibility criteria</Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>{tender.eligibility}</Typography>
                  </>
                )}
                <Typography variant="h6" gutterBottom>Timeline</Typography>
                <Stack spacing={0.5} sx={{ mb: 3 }}>
                  <Typography variant="body2">Published: {tender.publicationDate ? new Date(tender.publicationDate).toLocaleDateString('en-GB') : '—'}</Typography>
                  <Typography variant="body2">Submission deadline: {tender.submissionDeadline ? new Date(tender.submissionDeadline).toLocaleString('en-GB') : '—'}</Typography>
                  <Typography variant="body2">Bid opening: {tender.bidOpeningDate ? new Date(tender.bidOpeningDate).toLocaleString('en-GB') : '—'}</Typography>
                </Stack>
                {tender.contactDetails && (
                  <>
                    <Typography variant="h6" gutterBottom>Contact details</Typography>
                    <Typography variant="body1">{tender.contactDetails}</Typography>
                  </>
                )}
              </>
            )}

            {tab === 1 && (
              <>
                <Typography variant="h6" gutterBottom>Tender documents</Typography>
                {tender.documents?.length ? (
                  <List dense>
                    {tender.documents.map((d) => (
                      <ListItem key={d.id}
                        secondaryAction={
                          user ? <Button size="small" onClick={async () => {
                            const res = await api.get(`/tender-documents/${d.id}`, { responseType: 'blob' });
                            const url = window.URL.createObjectURL(new Blob([res.data]));
                            const a = document.createElement('a'); a.href = url; a.download = d.fileName; a.click();
                            window.URL.revokeObjectURL(url);
                          }}>Download</Button> : null
                        }>
                        <ListItemText primary={d.fileName} secondary={d.docType} />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography color="text.secondary">No documents attached to this tender.</Typography>
                )}
              </>
            )}

            {tab === 2 && (
              <>
                <Typography variant="h6" gutterBottom>Clarifications</Typography>
                {isVendor && (
                  <Box sx={{ mb: 3 }}>
                    <TextField label="Ask a question" value={question} onChange={(e) => setQuestion(e.target.value)} fullWidth multiline minRows={2} />
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1 }}>
                      <Button variant="contained" onClick={askQuestion}>Submit question</Button>
                      {qMsg && <Typography variant="caption" color="text.secondary">{qMsg}</Typography>}
                    </Stack>
                    <Divider sx={{ my: 3 }} />
                  </Box>
                )}
                {clarifications.length === 0 && <Typography color="text.secondary">No questions have been asked yet.</Typography>}
                <List>
                  {clarifications.map((c) => (
                    <ListItem key={c.id} alignItems="flex-start" sx={{ display: 'block' }}>
                      <Typography variant="subtitle2">Q: {c.question}</Typography>
                      {c.answer
                        ? <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>A: {c.answer}</Typography>
                        : <Typography variant="caption" color="warning.main">Awaiting response</Typography>}
                    </ListItem>
                  ))}
                </List>
              </>
            )}

            {tab === 3 && (
              <>
                <Typography variant="h6" gutterBottom>Submit a bid</Typography>
                {isVendor ? (
                  <>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      You can submit your technical and financial proposals for this tender. Financial proposals are
                      encrypted and stay sealed until the evaluation stage.
                    </Alert>
                    <Button variant="contained" onClick={() => navigate(`/vendor/tenders/${tender.id}/bid`)}>Go to bid submission</Button>
                  </>
                ) : (
                  <>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Bidding requires an approved vendor account. Register or sign in to submit a bid.
                    </Alert>
                    <Stack direction="row" spacing={2}>
                      <Button variant="contained" component={RouterLink} to="/register">Register to bid</Button>
                      <Button variant="outlined" component={RouterLink} to="/login">Sign in</Button>
                    </Stack>
                  </>
                )}
              </>
            )}
          </Box>
        </Paper>
      )}
    </PublicLayout>
  );
}
